import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ThumbsUp, ThumbsDown, Calculator } from "lucide-react";
import api from "@/lib/api";
import { formatINR } from "@/lib/utils-format";

export const ProductDeepDive = ({ slug, price, warrantyYears, compact = false }) => {
    const [data, setData] = useState(null);
    const [tenure, setTenure] = useState(12);

    useEffect(() => {
        api.get(`/products/${slug}/enrichment`).then((r) => setData(r.data)).catch(() => {});
    }, [slug]);

    if (!data) return null;
    const emi = price ? Math.round(price / tenure) : 0;

    // Rewrite warranty spec row for this product's tier (default 3-yr enrichment → 4-yr for 5IX/7IX)
    const specs = (data.specs || []).map((r) => {
        if (warrantyYears && /warranty/i.test(r[0] || "")) {
            return [r[0], `${warrantyYears}-year Signia India (extendable to ${warrantyYears + 1} years)`];
        }
        return r;
    });

    return (
        <section className="pb-16">
            <div className="container-x space-y-14">
                {/* Honesty panel */}
                <div className="grid md:grid-cols-2 gap-5 max-w-[920px]" data-testid="honesty-panel">
                    <div className="p-6 rounded-2xl bg-[#f0f7f4] border border-[#cfe3d8]">
                        <p className="flex items-center gap-2 font-medium text-[#1a5c3a]"><ThumbsUp size={16} /> Best for</p>
                        <ul className="mt-3 space-y-2 text-[14.5px] text-[#1a1a1a] list-disc pl-5">
                            {data.honesty.best_for.map((x, i) => <li key={i}>{x}</li>)}
                        </ul>
                    </div>
                    <div className="p-6 rounded-2xl bg-[#fdf3f0] border border-[#eed4cb]">
                        <p className="flex items-center gap-2 font-medium text-[#8a3b23]"><ThumbsDown size={16} /> Not ideal for</p>
                        <ul className="mt-3 space-y-2 text-[14.5px] text-[#1a1a1a] list-disc pl-5">
                            {data.honesty.not_ideal_for.map((x, i) => <li key={i}>{x}</li>)}
                        </ul>
                    </div>
                </div>

                {/* Specs */}
                <div className="max-w-[920px]">
                    <h2 className="font-serif text-[28px] text-[#003E4D] mb-4">Full specifications</h2>
                    <table className="w-full text-left text-[14.5px]" data-testid="specs-table">
                        <tbody>
                            {specs.map((r, i) => (
                                <tr key={i} className="border-b border-[#efece6]">
                                    <td className="py-2.5 pr-4 font-medium w-[42%]">{r[0]}</td>
                                    <td className="py-2.5 text-[#4a5568]">{r[1]}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Lifestyle suitability */}
                <div className="max-w-[920px]">
                    <h2 className="font-serif text-[28px] text-[#003E4D] mb-4">Lifestyle suitability</h2>
                    <div className="grid sm:grid-cols-2 gap-x-10 gap-y-3" data-testid="lifestyle-ratings">
                        {data.lifestyle.map((l, i) => (
                            <div key={i} className="flex items-center justify-between">
                                <span className="text-[14.5px]">{l.scenario}</span>
                                <span className="flex gap-1">
                                    {[1, 2, 3, 4, 5].map((n) => (
                                        <span key={n} className={`w-3 h-3 rounded-full ${n <= l.rating ? "bg-[#003E4D]" : "bg-[#e2ddd4]"}`} />
                                    ))}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Technology deep-dive — hidden in compact PDP mode (per user feedback: too long) */}
                {!compact && (
                    <div className="max-w-[820px]">
                        <h2 className="font-serif text-[28px] text-[#003E4D] mb-3">The technology inside</h2>
                        <div className="text-[15.5px] text-[#333] leading-relaxed space-y-3" dangerouslySetInnerHTML={{ __html: data.deep_dive }} />
                    </div>
                )}

                {/* EMI calculator */}
                {price > 0 && (
                    <div className="max-w-[820px] p-6 rounded-2xl bg-white border border-[#e2ddd4]" data-testid="emi-calculator">
                        <p className="flex items-center gap-2 font-medium text-[#003E4D]"><Calculator size={16} /> EMI calculator</p>
                        <div className="mt-4 flex flex-wrap gap-2">
                            {[6, 9, 12, 18, 24].map((t) => (
                                <button key={t} onClick={() => setTenure(t)} data-testid={`emi-tenure-${t}`}
                                    className={`text-[13px] rounded-full px-4 py-2 border ${tenure === t ? "bg-[#003E4D] text-white border-[#003E4D]" : "bg-white border-[#e2ddd4] text-[#003E4D]"}`}>
                                    {t} months
                                </button>
                            ))}
                        </div>
                        <p className="mt-4 text-[22px] font-serif text-[#003E4D]" data-testid="emi-amount">{formatINR(emi)}<span className="text-[14px] text-[#4a5568]">/month × {tenure}</span></p>
                        <p className="text-[12.5px] text-[#4a5568] mt-1">No-Cost EMI on eligible HDFC, ICICI, Axis, SBI, Kotak &amp; AmEx credit cards. Indicative — confirmed at checkout.</p>
                    </div>
                )}

                {/* Product FAQs */}
                <div className="max-w-[820px]">
                    <h2 className="font-serif text-[28px] text-[#003E4D] mb-4">Questions buyers ask</h2>
                    {data.faqs.map((f, i) => (
                        <details key={i} className="bg-white border border-[#e2ddd4] rounded-xl px-5 py-4 my-2" data-testid={`pdp-faq-${i}`}>
                            <summary className="cursor-pointer font-medium text-[15px]">{f.q}</summary>
                            <p className="mt-2 text-[14.5px] text-[#4a5568]">{f.a}</p>
                        </details>
                    ))}
                </div>

                {/* Internal links: articles + local availability — hidden in compact PDP mode */}
                {!compact && (
                    <div className="max-w-[920px]">
                        <h2 className="font-serif text-[24px] text-[#003E4D] mb-4">Learn before you buy</h2>
                        <div className="flex flex-wrap gap-2">
                            {data.related_articles.map((r) => (
                                <Link key={r.slug} to={`/learn/${r.slug}`} className="text-[13px] bg-white border border-[#e2ddd4] rounded-full px-4 py-2 text-[#003E4D] hover:border-[#003E4D]">{r.title}</Link>
                            ))}
                        </div>
                        <h2 className="font-serif text-[24px] text-[#003E4D] mt-8 mb-4">Available with local fitting in</h2>
                        <div className="flex flex-wrap gap-2" data-testid="local-availability-links">
                            {data.local_links.map((l) => (
                                <Link key={l.slug} to={l.url} className="text-[13px] bg-white border border-[#e2ddd4] rounded-full px-4 py-2 text-[#003E4D] hover:border-[#003E4D]">{l.name}</Link>
                            ))}
                            <Link to="/hearing-aids" className="text-[13px] bg-[#003E4D] text-white rounded-full px-4 py-2">All 159+ cities</Link>
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
};

export default ProductDeepDive;
